import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AuthLayout } from "../../components/auth/AuthLayout";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { StatusMessage } from "../../components/ui/StatusMessage";
import { useAuth } from "../../hooks/useAuth";
import { getRedirectForRole } from "../../lib/constants";
import { titleCase } from "../../lib/format";
import { inviteService } from "../../services/inviteService";
import type { InviteValidation } from "../../types/admin";

const GENERIC_ACTIVATION_ERROR = "We couldn't activate this invite. Please contact support or try again.";
const GENERIC_ACCEPTANCE_ERROR = "We couldn't accept this invite. Please contact support or try again.";

function getInviteActionError(error: unknown, fallback: string) {
  const message = error instanceof Error ? error.message : "";

  if (!message) return fallback;

  if (/column reference|ambiguous|SQLSTATE|schema cache|function .*does not exist|relation .*does not exist/i.test(message)) {
    return fallback;
  }

  return message;
}

function logInviteActionError(context: string, error: unknown) {
  if (import.meta.env.DEV) {
    console.error(context, error);
  }
}

export function InvitePage() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const { user, signIn, signOut, refreshAuth } = useAuth();
  const [invite, setInvite] = useState<InviteValidation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [existingMode, setExistingMode] = useState(false);
  const currentUserEmail = user?.email?.toLowerCase() ?? "";
  const invitedEmail = invite?.invited_email?.toLowerCase() ?? "";
  const hasLoggedInEmailMismatch = Boolean(
    invite?.is_valid && user && currentUserEmail && invitedEmail && currentUserEmail !== invitedEmail,
  );

  useEffect(() => {
    let mounted = true;

    async function loadInvite() {
      setLoading(true);
      setError(null);
      try {
        const result = await inviteService.validateInvite(token);
        if (!mounted) return;
        setInvite(result);
        setFullName(result?.invited_name ?? "");
        setEmail(result?.invited_email ?? "");
      } catch (caughtError) {
        if (mounted) {
          setError(caughtError instanceof Error ? caughtError.message : "Unable to validate invite.");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    void loadInvite();

    return () => {
      mounted = false;
    };
  }, [token]);

  async function acceptForSignedInUser() {
    if (hasLoggedInEmailMismatch) {
      setError(null);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const result = await inviteService.acceptForCurrentUser(token);
      await refreshAuth();
      navigate(getRedirectForRole(result?.role), { replace: true });
    } catch (caughtError) {
      logInviteActionError("Unable to accept invite.", caughtError);
      setError(getInviteActionError(caughtError, GENERIC_ACCEPTANCE_ERROR));
    } finally {
      setSubmitting(false);
    }
  }

  async function activateNewAccount(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await inviteService.activateNewAccount({ token, email, password, fullName });
      await signIn({ email, password });
      await refreshAuth();
      navigate(getRedirectForRole(invite?.role), { replace: true });
    } catch (caughtError) {
      logInviteActionError("Unable to activate invite.", caughtError);
      setError(getInviteActionError(caughtError, GENERIC_ACTIVATION_ERROR));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleMismatchSignOut() {
    setSubmitting(true);
    setError(null);
    try {
      await signOut();
    } finally {
      setSubmitting(false);
    }
  }

  async function acceptExistingAccount(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await signIn({ email, password });
      const result = await inviteService.acceptForCurrentUser(token);
      await refreshAuth();
      navigate(getRedirectForRole(result?.role), { replace: true });
    } catch (caughtError) {
      logInviteActionError("Unable to accept invite.", caughtError);
      setError(getInviteActionError(caughtError, GENERIC_ACCEPTANCE_ERROR));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout>
      <div>
        <p className="eyebrow">Invite activation</p>
        <h1 className="mt-3 text-3xl font-semibold tracking-normal text-ink">
          Join CoachGrid
        </h1>
        <p className="mt-3 text-base leading-7 text-graphite/70">
          Access is invite only. Confirm the invitation details before activating.
        </p>
      </div>

      <div className="mt-8 space-y-5">
        {loading ? <StatusMessage message="Checking invite..." /> : null}
        {error ? <StatusMessage message={error} tone="error" /> : null}
        {!loading && !invite ? (
          <StatusMessage message="This invite could not be found." tone="error" />
        ) : null}
        {invite && !invite.is_valid ? (
          <StatusMessage
            message={`This invite cannot be used. Reason: ${invite.invalid_reason ?? invite.status}.`}
            tone="error"
          />
        ) : null}

        {invite?.is_valid ? (
          <div className="rounded-lg border border-line bg-field p-5 shadow-soft">
            <p className="text-sm font-semibold text-ink">{invite.invited_email}</p>
            <p className="mt-2 text-sm text-graphite/70">
              Role: {titleCase(invite.role)} - Workspace: {invite.tenant_name ?? "Platform"}
            </p>
          </div>
        ) : null}

        {hasLoggedInEmailMismatch ? (
          <div className="space-y-4 rounded-lg border border-amber-900/20 bg-amber-50 p-5 text-sm text-amber-950">
            <p>
              You are logged in as {user?.email}, but this invite is for {invite?.invited_email}. Please sign out to accept this invite.
            </p>
            <Button fullWidth variant="secondary" isLoading={submitting} onClick={() => void handleMismatchSignOut()}>
              Sign out
            </Button>
          </div>
        ) : null}

        {invite?.is_valid && user && !hasLoggedInEmailMismatch ? (
          <Button fullWidth isLoading={submitting} onClick={() => void acceptForSignedInUser()}>
            Accept invite
          </Button>
        ) : null}

        {invite?.is_valid && !user ? (
          <form className="space-y-5" onSubmit={existingMode ? acceptExistingAccount : activateNewAccount}>
            {!existingMode ? (
              <Input label="Full name" value={fullName} onChange={(event) => setFullName(event.target.value)} required />
            ) : null}
            <Input label="Email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
            <Input label={existingMode ? "Password" : "Create password"} type="password" value={password} onChange={(event) => setPassword(event.target.value)} required />
            <Button fullWidth isLoading={submitting} type="submit">
              {existingMode ? "Sign in and accept" : "Activate account"}
            </Button>
          </form>
        ) : null}

        {invite?.is_valid && !user ? (
          <button className="link text-sm" onClick={() => setExistingMode((value) => !value)} type="button">
            {existingMode ? "Create a new invited account" : "Already have an account? Sign in to accept"}
          </button>
        ) : null}

        <p className="text-sm text-graphite/70">
          <Link className="link" to="/login">Return to login</Link>
        </p>
      </div>
    </AuthLayout>
  );
}
